// ~/lib/stores/cartStore.ts
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

interface CartItem {
  id: string
  name: string
  design: string
  m2: number
  priceM2: number
  productionDays: string
  quantity: number
  includesProtection: boolean
  includesJoint: boolean
  protectionPriceM2: number
  jointPriceM2: number
  image: string
}

interface Address {
  locale?: string
  firstName: string
  lastName: string
  companyName?: string
  nifCifNie: string
  street: string
  number?: string
  blockFloorDoor?: string
  city?: string
  province: string
  island?: string
  country: string
  postalCode: string
  countryPrefix: string
  phone: string
  email: string
  orderNotes?: string
  projectRef?: string
  differentBillingAddress: boolean
  // Billing address fields (only used when differentBillingAddress is true)
  billingFirstName?: string
  billingLastName?: string
  billingCompanyName?: string
  billingNifCifNie?: string
  billingStreet?: string
  billingNumber?: string
  billingBlockFloorDoor?: string
  billingProvince?: string
  billingIsland?: string
  billingCountry?: string
  billingPostalCode?: string
  billingCountryPrefix?: string
  billingPhone?: string
  billingEmail?: string
  termsAndConditions: boolean
  receiveOffers: boolean
}

interface PaymentMethod {
  cardNumber: string
  expiryDate: string
  cvv: string
  cardholderName: string
}

interface CartState {
  items: CartItem[]
  address: Address | null
  paymentMethod: PaymentMethod | null
  hasHydrated: boolean
  setHasHydrated: (hasHydrated: boolean) => void
  addItem: (item: CartItem) => void
  removeItem: (id: string) => void
  updateQuantity: (id: string, quantity: number) => void
  setShippingAddress: (address: Address) => void
  setPaymentMethod: (payment: PaymentMethod) => void
  total: number
}

export const useCartStore = create<CartState>()(
  persist(
    set => ({
      items: [],
      address: null,
      paymentMethod: null,
      hasHydrated: false,
      setHasHydrated: hasHydrated => set({ hasHydrated }),
      addItem: item => set(state => ({ items: [...state.items, item] })),
      total: 0,
      removeItem: id =>
        set(state => ({
          items: state.items.filter(item => item.id !== id),
        })),
      updateQuantity: (id, quantity) =>
        set(state => ({
          items: state.items.map(item =>
            item.id === id ? { ...item, quantity } : item,
          ),
        })),
      setShippingAddress: address => set({ address }),
      setPaymentMethod: payment => set({ paymentMethod: payment }),
    }),
    {
      name: 'todobarro-cart-storage', // unique name
      storage: createJSONStorage(() => localStorage), // use localStorage
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
      // Only persist address and paymentMethod, not the mock items
      partialize: state => ({
        address: state.address,
        paymentMethod: state.paymentMethod,
      }),
    },
  ),
)
